import { App, Button, Flex, Modal } from "antd";
import { FC, useRef, useState } from "react";
import apis from "@/apis/requirement";
import { TimeRangeSelector, TimeRangeSelectorRef } from "./TimeRangeSelector";
import { Requirement, RequirementItem } from "@/types/requirement";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { t } from "i18next";
import SText from "@/styles/SText";
import CommonTable from "@/components/CommonTable";

export enum RequestPriceOptCbEnum {
  ONE_REQUEST = '1',
  MUILTY_REQUEST = '2',
  FINISH = '3'
}

interface IRequestPriceOpt {
  detail: Requirement;
  id: string;
  mrd: number;
  items: Array<RequirementItem>;
  cb?: (cbType: RequestPriceOptCbEnum, cbData?: Requirement | undefined) => void
}

const RequestPriceOpt: FC<IRequestPriceOpt> = ({ detail, id, mrd, items, cb }) => {
  const { notification, modal } = App.useApp();
  const timeSelectorRef = useRef<TimeRangeSelectorRef>(null);
  const [oneRequestOpen, setOneRequestOpen] = useState<boolean>(false);
  const [multiRequestOpen, setMultiRequestOpen] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [finishLoading, setFinishLoading] = useState<boolean>(false);

  const handleRequestPrice = () => {
    if (timeSelectorRef.current) {
      const deadlineTimestamp = timeSelectorRef.current.getDeadlineTime().valueOf();

      apis
        .requirementGenerate(id, {
          deadline: deadlineTimestamp,
          mrd,
          supplierPrice: "",
          supplierTime: "",
        })
        .then((res) => {
          if (res.body && res.body?.length > 0) {
            notification.success({ placement: 'top', message: t('requirement.sendQutote') });
            setOneRequestOpen(false);
            cb && cb(RequestPriceOptCbEnum.ONE_REQUEST)
          } else if (res.body && res.body?.length === 0) {
            notification.success({ placement: 'top', message: t('requirement.noSupplier') });
          }
        })
        .catch(() => {
          notification.error({ placement: 'top', message: t('requirement.qutoteSednFail') });
        });
    }
  };

  const handleBatchRequest = () => {
    if (timeSelectorRef.current) {
      const deadlineTimestamp = timeSelectorRef.current.getDeadlineTime().valueOf();

      apis
        .itemsQuotationRequestGenerate({
          deadline: deadlineTimestamp,
          mrd,
          supplierPrice: "",
          supplierTime: "",
        }, selectedRowKeys)
        .then((res) => {
          if (res.body && res.body?.length > 0) {
            notification.success({ placement: 'top',
              message: t('requirement.sendQutotePro', { len: selectedRowKeys.length }),
              description: `${t('requirement.ddl')}: ${dayjs(deadlineTimestamp).format("YYYY-MM-DD HH:mm:ss")}`
            });
            cb && cb(RequestPriceOptCbEnum.MUILTY_REQUEST)
            setMultiRequestOpen(false);
            setSelectedRowKeys([]);
          } else if (res.body && res.body?.length === 0) {
            notification.success({ placement: 'top', message: t('requirement.noSupplier') });
          }
        })
        .catch(() => {
          notification.error({ placement: 'top', message: t('requirement.qutoteSednFail') });
        });
    }
  };

  const handleCancel = () => {
    timeSelectorRef?.current?.resetTime();
    setOneRequestOpen(false);
    setMultiRequestOpen(false);
    setSelectedRowKeys([]);
  };

  const onFinish = () => {
    setFinishLoading(true);
    apis.finishQuotationRequest(id)
      .then((res) => {
        notification.success({ placement: 'top', message: t('requirement.autoPurcheseSucc') });
        cb && cb(RequestPriceOptCbEnum.FINISH, res)
        handleCancel();
      })
      .finally(() => {
        setFinishLoading(false);
      })
  };

  const handleFinish = () => {
    modal.confirm({
      title: t("requirement.purTip"),
      content: t("requirement.purContent"),
      onOk() {
        onFinish()
      },
    });
  }

  const columns: ColumnsType<RequirementItem> = [
    {
      title: t('product.code'),
      key: 'code',
      render: (_, record) => <span>{record.product?.code || ''}</span>,
    },
    {
      title: t('product.brand1'),
      key: 'name',
      render: (_, record) => <span>{record.product?.name || ''}</span>,
    },
    {
      title: t('product.name'),
      key: 'model',
      render: (_, record) => <span>{record.product?.model || ''}</span>,
    },
    {
      title: <SText text={t("product.preReady")} />,
      width: 100,
      render(value, row, rowIndex) {
        return (row?.product?.preReady || 0) === 1 ? <SText text={t('product.isTrue')} /> : <SText text={t('product.isFalse')} />
      },
    },
    // {
    //   title: t('counting.quantity'),
    //   key: 'count',
    //   render: (_, record) => <span>{record.useAmount ?? ''}</span>,
    // },
    {
      title: t('requirement.requirmentQuantity'),
      width: 120,
      key: 'requirmentQuantity',
      render: (_, record) => <span>{record.purCount ?? ''}</span>,
    },
  ];

  return (
    <Flex gap={10}>
      {t('common.opt')}:
      <Button type="primary" onClick={() => setOneRequestOpen(true)}>
        {t('requirement.onKeyQutote')}
      </Button>
      <Button type="link" onClick={() => setMultiRequestOpen(true)}>
        {t('requirement.subQutote')}
      </Button>
      <Button type='dashed' onClick={() => handleFinish()} loading={finishLoading}>
        {t('requirement.onKeyPurchese')}
      </Button>

      <Modal
        title={t('requirement.onKeyPrice')}
        open={oneRequestOpen}
        onOk={handleRequestPrice}
        onCancel={handleCancel}
        width={600}
      >
        <TimeRangeSelector ref={timeSelectorRef} />
      </Modal>

      <Modal
        title={t('requirement.subPrice')}
        open={multiRequestOpen}
        onOk={handleBatchRequest}
        onCancel={handleCancel}
        width={800}
      >
        <TimeRangeSelector ref={timeSelectorRef} />
        {multiRequestOpen && <div style={{ marginTop: 24 }}>
          <CommonTable<RequirementItem>
            rowKey="id"
            columns={columns}
            dataSource={items}
            pagination={false}
            virtual
            bordered={false}
            rowSelection={{
              selectedRowKeys,
              getCheckboxProps: (row: RequirementItem) => ({
                disabled: !row?.purCount || row.purCount === '0'
              }),
              onChange: (keys) => setSelectedRowKeys(keys as string[]),
            }}
            scroll={{ x: 800, y: 600 }}
          />
        </div>}
      </Modal>
    </Flex>
  );
};

export default RequestPriceOpt;
